import * as Sentry from '@sentry/react';
import {
  HtmlStepperForm,
  HtmlStepperFormData,
  HtmlStepperUpdate,
  HtmlStepperUpdateTarget,
  HtmlStepperUpdateType,
} from '@superfunnel/types';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { uid } from 'uid';
import { applyStepperChange } from '../components/HtmlStepperEditor/utils/apply-stepper-changes';
import { db } from '../Firebase';
import { getFormProductionData as getFormProductionDataService } from '../services/FormsService';
import {
  markQuizHasChangesToPublish,
  publishQuiz,
} from '../services/QuizService';
import { emptyConverter } from '../utils';
import { track } from '../utils/analytics';
import { LimitedStack } from '../utils/limited-stack';
import { UserContext } from './UserContext';

type CreateStepperUpdateFn = (
  formId: string,
  target: HtmlStepperUpdateTarget,
  elementPath: string | undefined,
  content: unknown,
  type: HtmlStepperUpdateType,
  before?: HtmlStepperFormData,
) => HtmlStepperUpdate | undefined;

type ApplyFormChangeFn = (update: HtmlStepperUpdate) => void;

export interface HtmlStepperUpdaterContextProps {
  // Save status
  saveStatus: 'none' | 'saving' | 'done';
  setSaveStatus: (status: 'none' | 'saving' | 'done') => void;
  lastSavedAt: Date | null;

  // Publishing
  publishing: boolean;
  publish: (formId: string) => Promise<void>;

  // Discarding
  discarding: boolean;
  discardChanges: (formId: string) => Promise<void>;

  // Update functions
  createStepperUpdate: CreateStepperUpdateFn;
  applyFormChange: ApplyFormChangeFn;

  // Undo/Redo
  canRevert: boolean;
  canForward: boolean;
  revertLatestUpdate: () => boolean;
  forwardRevert: () => boolean;
  clearHistory: () => void;

  // Apply changes synchronously (for preview updates)
  applyChangesSync: (
    formData: HtmlStepperFormData,
    extra?: HtmlStepperUpdate[],
  ) => HtmlStepperFormData;
}

export const HtmlStepperUpdaterContext =
  createContext<HtmlStepperUpdaterContextProps>({
    saveStatus: 'none',
    setSaveStatus: () => {},
    lastSavedAt: null,
    publishing: false,
    publish: async () => {},
    discarding: false,
    discardChanges: async () => {},
    createStepperUpdate: () => undefined,
    applyFormChange: () => {},
    canRevert: false,
    canForward: false,
    revertLatestUpdate: () => false,
    forwardRevert: () => false,
    clearHistory: () => {},
    applyChangesSync: (formData) => formData,
  });

interface HtmlStepperUpdaterProviderProps {
  accountId: string;
  quizId: string;
  children: ReactNode;
}

export function HtmlStepperUpdaterProvider({
  accountId,
  quizId,
  children,
}: HtmlStepperUpdaterProviderProps) {
  const { selectedAccount } = useContext(UserContext);
  const [saveStatus, setSaveStatus] = useState<'none' | 'saving' | 'done'>(
    'none',
  );
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  // Pending updates for batching
  const [updates, setUpdates] = useState<HtmlStepperUpdate[]>([]);

  // History stacks for undo/redo
  const [history, setHistory] = useState<LimitedStack<HtmlStepperUpdate>>(
    new LimitedStack(20),
  );
  const [revertHistory, setRevertHistory] = useState<
    LimitedStack<HtmlStepperUpdate>
  >(new LimitedStack(20));

  // Create a stepper update object
  const createStepperUpdate: CreateStepperUpdateFn = useCallback(
    (formId, target, elementPath, content, type, before) => {
      if (!selectedAccount) return;

      const update: HtmlStepperUpdate = {
        id: uid(8),
        accountId: selectedAccount.id,
        formId,
        target,
        elementPath,
        content,
        type,
        before,
      };

      return update;
    },
    [selectedAccount],
  );

  // Apply a change to the form data
  const applyChange = useCallback(
    (update: HtmlStepperUpdate, data: HtmlStepperFormData): boolean => {
      return applyStepperChange(update, data);
    },
    [],
  );

  // Apply changes synchronously (for preview updates)
  const applyChangesSync = useCallback(
    (
      data: HtmlStepperFormData,
      extra?: HtmlStepperUpdate[],
    ): HtmlStepperFormData => {
      const allUpdates = [...updates, ...(extra ?? [])];
      allUpdates.forEach((update) => applyChange(update, data));
      return data;
    },
    [applyChange, updates],
  );

  // Apply a form change (add to update queue)
  const applyFormChange: ApplyFormChangeFn = useCallback(
    (update: HtmlStepperUpdate) => {
      if (!selectedAccount) return;

      setSaveStatus('saving');
      setUpdates((u) => [...u, update]);

      // If the update has a before state, add to history for undo
      if (update.before) {
        setHistory((h) => {
          const newHistory = new LimitedStack(h);
          newHistory.push(update);
          return newHistory;
        });
      }

      // Clear redo history on new change
      setRevertHistory(new LimitedStack(20));
    },
    [selectedAccount],
  );

  // Clear history
  const clearHistory = useCallback(() => {
    setHistory(new LimitedStack(20));
    setRevertHistory(new LimitedStack(20));
  }, []);

  // Revert latest update (undo)
  const revertLatestUpdate = useCallback(() => {
    if (!history.getSize()) return false;

    const newHistory = new LimitedStack(history);
    const changeToRevert = newHistory.pop();

    setHistory(newHistory);

    if (!changeToRevert?.before) return false;

    const beforeState = changeToRevert.before as HtmlStepperFormData;

    const revertUpdate = createStepperUpdate(
      changeToRevert.formId,
      changeToRevert.target,
      changeToRevert.elementPath,
      beforeState, // Content is the before state for revert
      'revertChange',
      undefined, // No before for revert updates
    );

    if (!revertUpdate) return false;

    setSaveStatus('saving');
    setUpdates((u) => [...u, revertUpdate]);
    setRevertHistory((rh) => {
      const newRevertHistory = new LimitedStack(rh);
      newRevertHistory.push({ ...changeToRevert, id: uid(8) });
      return newRevertHistory;
    });

    return true;
  }, [createStepperUpdate, history]);

  // Forward revert (redo)
  const forwardRevert = useCallback(() => {
    if (!revertHistory.getSize()) return false;

    const newRevertHistory = new LimitedStack(revertHistory);
    const forwardChange = newRevertHistory.pop();

    setRevertHistory(newRevertHistory);

    if (!forwardChange) return false;

    setSaveStatus('saving');
    setUpdates((u) => [...u, forwardChange]);
    setHistory((h) => {
      const newHistory = new LimitedStack(h);
      newHistory.push(forwardChange);
      return newHistory;
    });

    return true;
  }, [revertHistory]);

  // Save form to Firestore (fetches fresh, applies updates, saves)
  // Groups updates by formId and saves each group to its respective form
  const _saveForm = useCallback(
    async (formUpdates: HtmlStepperUpdate[]) => {
      // Group updates by formId
      const updatesByFormId = new Map<string, HtmlStepperUpdate[]>();
      for (const update of formUpdates) {
        const existing = updatesByFormId.get(update.formId) || [];
        existing.push(update);
        updatesByFormId.set(update.formId, existing);
      }

      let anyUpdated = false;

      // Process each form's updates
      for (const [formId, updates] of updatesByFormId) {
        const formRef = doc(
          db,
          'accounts',
          accountId,
          'quizzes',
          quizId,
          'forms',
          formId,
        ).withConverter(emptyConverter<HtmlStepperForm>());

        const formDoc = await getDoc(formRef);
        const development = formDoc.data()?.development;
        if (!formDoc.exists() || !development) continue;

        let updated = false;

        for (const update of updates) {
          updated = applyChange(update, development) || updated;
        }

        if (updated) {
          await updateDoc(formRef, {
            development: development as any,
            changesToPublish: true,
            updatedAt: new Date(),
          });
          anyUpdated = true;
        }
      }

      // If any form was updated, also mark the Quiz and DynamicPage as having changes
      if (anyUpdated) {
        await markQuizHasChangesToPublish(accountId, quizId);
      }

      return { updated: anyUpdated };
    },
    [accountId, applyChange, quizId],
  );

  // Debounced save effect
  useEffect(() => {
    if (!updates.length) return;

    const timer = setTimeout(async () => {
      const pendingUpdates = [...updates];
      if (!pendingUpdates.length) return;

      try {
        await _saveForm(pendingUpdates);
        // Only clear updates after successful save
        setUpdates((currentUpdates) =>
          currentUpdates.filter((u) => !pendingUpdates.includes(u)),
        );
        setSaveStatus('done');
        setLastSavedAt(new Date());
      } catch (e) {
        Sentry.captureException(e);
        console.error('[HtmlStepperUpdater] Error saving form:', e);
        setSaveStatus('none');
      }
    }, 1500);

    return () => {
      clearTimeout(timer);
    };
  }, [_saveForm, updates]);

  // Publish changes (copy development to production)
  const publish = useCallback(
    async (formId: string) => {
      if (!selectedAccount || publishing) return;

      // Save any pending updates first
      if (updates.length) {
        try {
          await _saveForm(updates);
          setUpdates([]);
        } catch (e) {
          Sentry.captureException(e);
          console.error('[HtmlStepperUpdater] Error saving before publish:', e);
          return;
        }
      }

      track('publish_quiz', {
        quizId,
        formId,
      });

      setPublishing(true);
      try {
        await publishQuiz(accountId, quizId);
        setSaveStatus('done');
      } catch (e) {
        Sentry.captureException(e);
        console.error('[HtmlStepperUpdater] Error publishing form:', e);
      } finally {
        setPublishing(false);
      }
    },
    [_saveForm, accountId, publishing, quizId, selectedAccount, updates],
  );

  // Discard changes (reset development to production from Cloud Storage)
  const discardChanges = useCallback(
    async (formId: string) => {
      if (!selectedAccount || discarding) return;

      // Clear any pending updates
      setUpdates([]);

      setDiscarding(true);
      try {
        // Fetch production data from Cloud Storage via backend endpoint
        const productionData = await getFormProductionDataService(
          accountId,
          quizId,
          formId,
        );

        const formRef = doc(
          db,
          'accounts',
          accountId,
          'quizzes',
          quizId,
          'forms',
          formId,
        ).withConverter(emptyConverter<HtmlStepperForm>());

        // Reset development to production
        await updateDoc(formRef, {
          development: productionData as any,
          changesToPublish: false,
          updatedAt: new Date(),
        });

        setSaveStatus('done');
      } catch (e) {
        Sentry.captureException(e);
        console.error('[HtmlStepperUpdater] Error discarding changes:', e);
      } finally {
        setDiscarding(false);
      }
    },
    [accountId, discarding, quizId, selectedAccount],
  );

  return (
    <HtmlStepperUpdaterContext.Provider
      value={{
        saveStatus,
        setSaveStatus,
        lastSavedAt,
        publishing,
        publish,
        discarding,
        discardChanges,
        createStepperUpdate,
        applyFormChange,
        canRevert: history.getSize() > 0,
        canForward: revertHistory.getSize() > 0,
        revertLatestUpdate,
        forwardRevert,
        clearHistory,
        applyChangesSync,
      }}
    >
      {children}
    </HtmlStepperUpdaterContext.Provider>
  );
}

export const useHtmlStepperUpdater = () => {
  return useContext(HtmlStepperUpdaterContext);
};
